import * as React from 'react';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridColumnMenuProps,
  GridColumnMenu,
  GridLoadingOverlayProps,
  LoadingOverlayPropsOverrides,
  GridRenderCellParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { CircularProgress, LinearProgress } from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  searchVendors,
  setSearchInput,
  setSearchLoading,
  setSelectedVendors,
} from '../../../../state/vendorSearchModalSlice';
import { useDispatch, useSelector } from '../../../../state';
import type { VendorSearchInput } from '@repo/api-contract';
import { useCallback } from 'react';
import { debounce } from '@mui/material/utils';
import SearchByDropdown from './SearchByDropdown';
import { useTranslation } from 'react-i18next';
import hexToRGBA from '../../../../util/converters/hexToRGBA';
import { initialPageSize } from './constants';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../../../util/hooks';
import type { DataGridTableId } from '../../../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'search-vendors' satisfies DataGridTableId;
const KEY_VENDOR_BROWSE = 'team.Components.VendorBrowseTable';
const KEY_GLOBAL_COMMON = 'team.Global.Common';

interface LoaderProps
  extends Omit<GridLoadingOverlayProps, 'color'>,
    LoadingOverlayPropsOverrides {
  color?: string;
}

const Loader = (props: LoaderProps) => {
  return <LinearProgress color={'primary'} />;
};

function CustomToolbar({ pageSize }: { pageSize: number }) {
  const dispatch = useDispatch();
  const { t: tMCS } = useTranslation([], {
    keyPrefix: 'team.Modals.VendorsSearch',
  });
  const { searchInput, searchBy, searchLoading, resp } = useSelector(
    (store) => store.vendorSearchModalSlice,
  );
  const { team } = useSelector((store) => store.userSlice);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: VendorSearchInput = {
        query: searchQuery,
        searchBy,
        size: pageSize,
        page: 0,
      };
      dispatch(searchVendors(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <SearchByDropdown />
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setSearchLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label={tMCS('Search for vendors...')}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <React.Fragment>
              {searchLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
            </React.Fragment>
          ),
        }}
      />
    </GridToolbarContainer>
  );
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuColumnsVendor: null,
        columnMenuFilterVendor: null,
      }}
    />
  );
}

interface TableProps {
  multiSelect: boolean;
}

export default function Table({ multiSelect }: TableProps) {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { resp, searchLoading, searchInput, searchBy } = useSelector(
    (store) => store.vendorSearchModalSlice,
  );

  // 2) Handle row selection
  const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
    // If single selection is desired but newSelectionModel has >1 items,
    // keep only the last item.
    let finalSelectionModel = newSelectionModel;
    if (!multiSelect && newSelectionModel.length > 1) {
      const lastId = newSelectionModel[newSelectionModel.length - 1];
      if (lastId !== undefined) {
        finalSelectionModel = [lastId];
      }
    }

    // Filter the rows to get the full objects of the selected vendors
    const rows = resp ? resp.results : [];
    const selected = rows.filter((row) =>
      finalSelectionModel.includes(row.VendorId),
    );
    dispatch(setSelectedVendors(selected));
  };

  const baseColumns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Vendor Code',
        type: 'string',
        width: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Code"
            keyPrefix={KEY_VENDOR_BROWSE}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'companyName',
        headerName: 'Company Name',
        width: 210,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Company Name"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'currentBalance',
        headerName: 'Balance',
        width: 150,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Balance"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(params.value)}
          </span>
        ),
      },
      {
        field: 'contact',
        headerName: 'Contact',
        width: 180,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Contact"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'city',
        headerName: 'City',
        type: 'string',
        width: 140,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="City"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'state',
        headerName: 'State',
        sortable: false,
        width: 100,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="State"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'zip',
        headerName: 'Zip',
        type: 'string',
        width: 110,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Zip"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'telephone',
        headerName: 'Tel. No.',
        type: 'string',
        width: 160,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Tel. No."
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        type: 'string',
        width: 160,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Email"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
    ],
    [],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: multiSelect
      ? GRID_CHECKBOX_COLUMN_ORDER
      : EMPTY_LEADING_GRID_COLUMNS,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <DataGridPro
      apiRef={apiRef}
      columnHeaderHeight={56}
      sx={{
        border: 'none',
        padding: '1rem',
        '.MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
          backgroundColor: '#ECECEC',
        },
        '.MuiDataGrid-row.Mui-selected': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-scrollbar--horizontal': { display: 'none' },
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
          whiteSpace: 'normal',
          lineHeight: 1.2,
        },
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
      }}
      rows={
        resp
          ? resp.results.map((vendor) => ({
              ...vendor,
              id: vendor.VendorId,
              code: vendor.code,
              companyName: vendor.details.companyName || '',
              currentBalance: vendor.payment?.currentBalance || '',
              contact: vendor.receivable?.contactName || '',
              city: vendor.details.city || '',
              state: vendor.details.state || '',
              zip: vendor.details.zipCode || '',
              telephone: vendor.details.phone || '',
              email: vendor.receivable?.email || '',
              vendor: vendor,
            }))
          : []
      }
      onRowSelectionModelChange={handleSelectionChange}
      checkboxSelection={multiSelect} // show checkboxes only if multiSelect=true
      disableRowSelectionOnClick={multiSelect ? true : false}
      loading={searchLoading}
      columns={columns}
      columnVisibilityModel={persistedPrefs.columnVisibilityModel}
      onColumnVisibilityModelChange={
        persistedPrefs.onColumnVisibilityModelChange
      }
      onColumnWidthChange={persistedPrefs.onColumnWidthChange}
      onColumnOrderChange={persistedPrefs.onColumnOrderChange}
      pagination
      paginationMode="server"
      paginationModel={{
        page: resp?.page || 0,
        pageSize: resp?.size || 0,
      }}
      rowCount={resp?.total || 0}
      pageSizeOptions={[initialPageSize, 25, 50, 100]}
      onPaginationModelChange={(newModel) => {
        const payload: VendorSearchInput = {
          query: searchInput,
          searchBy,
          page: newModel.page,
          size: newModel.pageSize,
        };
        dispatch(searchVendors(payload));
      }}
      pinnedColumns={
        multiSelect ? { left: [GRID_CHECKBOX_SELECTION_FIELD] } : undefined
      }
      disableColumnMenu
      autoHeight={false}
      slots={{
        loadingOverlay: Loader,
        toolbar: () => (
          <CustomToolbar pageSize={resp?.size || initialPageSize} />
        ),
        columnMenu: CustomColumnMenu,
      }}
    />
  );
}
